/**
 * Verification API service — handles Aadhaar verification uploads and status checks.
 */
import { apiClient } from '@/lib/api-client';
import type {
  VerificationRecord,
  VerificationUploadResponse,
  VerificationReviewRequest,
} from '@/types/verification';

const BASE = '/api/admin/verification';

export const verificationApi = {
  /**
   * Upload Aadhaar verification documents (multipart form-data).
   */
  uploadAadhaar(
    aadhaarNumber: string,
    frontImage: File,
    backImage: File,
  ): Promise<VerificationUploadResponse> {
    const formData = new FormData();
    formData.append('aadhaarNumber', aadhaarNumber);
    formData.append('frontImage', frontImage);
    formData.append('backImage', backImage);

    return apiClient.postForm<VerificationUploadResponse>(`${BASE}/upload`, formData);
  },

  /**
   * Get verification status for a user.
   */
  getStatus(userId: string): Promise<VerificationRecord | { status: string; message: string }> {
    // If userId is not a valid UUID (e.g. internal DB ID), use 'me' to let backend resolve via token
    const idParam = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(userId) ? userId : 'me';
    return apiClient.get(`${BASE}/status/${idParam}`);
  },

  // ── Admin methods ──

  /**
   * List verification queue (paginated, filterable by status).
   */
  getQueue(params?: {
    status?: string;
    page?: number;
    size?: number;
  }): Promise<{
    content: VerificationRecord[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }> {
    return apiClient.get(`${BASE}/queue`, { params: params as Record<string, any> });
  },

  /**
   * Get pending verification count.
   */
  getPendingCount(): Promise<{ count: number }> {
    return apiClient.get(`${BASE}/pending/count`);
  },

  /**
   * Get a pre-signed URL for direct document access.
   */
  fetchDocumentPresignedUrl(verificationId: string, side: 'front' | 'back'): Promise<{ url: string }> {
    return apiClient.get(`${BASE}/document/${verificationId}/${side}/url`);
  },

  /**
   * Review a verification (approve/reject with notes).
   */
  reviewVerification(id: string, request: VerificationReviewRequest): Promise<VerificationRecord> {
    return apiClient.put(`${BASE}/${id}/review`, request);
  },
};
